/**
 * Shown when the spec base URL is a placeholder (e.g. templated or example.com).
 * Placed above credentials + Generate so it is hard to miss.
 */

import { motion } from "framer-motion";
import { AlertTriangle, Globe } from "lucide-react";
import { baseUrlInputPlaceholder } from "../lib/baseUrl";

type Props = {
  specBaseUrl?: string;
  value: string;
  onChange: (url: string) => void;
  /** Render inside a parent card (no outer glass wrapper). */
  embedded?: boolean;
  /** Separator when stacked below another setup block in the same card. */
  withTopDivider?: boolean;
};

function BaseUrlConfigFields({ specBaseUrl, value, onChange }: Omit<Props, "embedded" | "withTopDivider">) {
  const placeholder = baseUrlInputPlaceholder(specBaseUrl);
  const specSnippet = (specBaseUrl ?? "").trim();

  return (
    <>
      <div className="flex items-start gap-3 mb-6">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-muted flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-muted-foreground" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground leading-tight">
            API base URL required
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {specSnippet ? (
              <>
                The spec only lists a placeholder (
                <code className="text-[11px]">{specSnippet}</code>
                ). Enter your real API base URL before generating.
              </>
            ) : (
              <>The spec does not include a usable server URL. Enter your API base URL before generating.</>
            )}
          </p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">
          <span className="inline-flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" />
            Base URL
          </span>
        </label>
        <input
          type="url"
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 transition"
        />
      </div>
    </>
  );
}

export default function BaseUrlConfigStep({
  specBaseUrl,
  value,
  onChange,
  embedded = false,
  withTopDivider = false,
}: Props) {
  const fields = (
    <BaseUrlConfigFields specBaseUrl={specBaseUrl} value={value} onChange={onChange} />
  );

  if (embedded) {
    return (
      <div className={withTopDivider ? "pt-6 border-t border-border" : undefined}>{fields}</div>
    );
  }

  return (
    <motion.section
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="py-0 px-6"
    >
      <div className="container max-w-2xl mx-auto">
        <div className="glass-strong rounded-2xl p-7 shadow-soft">{fields}</div>
      </div>
    </motion.section>
  );
}
